import React, { useMemo } from 'react';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import {
  getFinanceInstancesFromStorage,
  getFinanceInstanceSeriesFromStorage,
  getFinanceInstanceCurveMetaFromStorage,
} from '../../kpi-engine/budgetDataAdapter';
import { indicatorBucket, bucketToColor } from '../../utils/indicatorBuckets';
import { preflightStore, storeKey } from '../../Data/store';
import { usePreflightLoader } from '../../hooks/usePreflightLoader';

/**
 * BudgetOverviewBar — Tier 1 budget health indicator.
 *
 * Shows a progress bar of actual spend vs total budget,
 * colored by the finance KPI indicator.
 *
 * Data resolution order for total budget:
 *   1. curveMeta.total_budget (explicit setting)
 *   2. Last baseline_budget value in the series (cumulative planned total)
 *   3. Falls back to score-only display if neither exists
 *
 * Uses the first budget instance found (prefers kpi_cost_progress_v1).
 */
type Props = {
  projectGuid: string;
  selectedItem: any;
};

const fmtCurrency = (amount: number, currency: string): string => {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currency || 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `${currency || '$'}${amount.toLocaleString()}`;
  }
};

const BudgetOverviewBar: React.FC<Props> = ({ projectGuid, selectedItem }) => {
  const { t } = useAppTranslation('kpi');

  // ── BOM cost rollup from preflight items ──
  const { preflightVersion } = usePreflightLoader(projectGuid, !!projectGuid);
  const { bomEstimatedTotal, bomActualTotal } = useMemo(() => {
    if (!projectGuid) return { bomEstimatedTotal: 0, bomActualTotal: 0 };
    const items = preflightStore.get(storeKey(projectGuid));
    if (!items || items.length === 0) return { bomEstimatedTotal: 0, bomActualTotal: 0 };
    let estimated = 0;
    let actual = 0;
    for (const item of items) {
      if ((item as any).state === 'withdrawn') continue;
      const est =
        (item as any).estimated_total ??
        ((item as any).unit_cost ?? 0) * ((item as any).target_quantity ?? 0);
      const act =
        (item as any).actual_total ??
        ((item as any).actual_cost ?? 0) * ((item as any).current_quantity ?? 0);
      estimated += Number(est) || 0;
      actual += Number(act) || 0;
    }
    return { bomEstimatedTotal: estimated, bomActualTotal: actual };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectGuid, preflightVersion]);

  if (!projectGuid) return null;

  const instances = getFinanceInstancesFromStorage(projectGuid);
  if (instances.length === 0) return null;

  // Prefer kpi_cost_progress_v1, fall back to first instance
  const targetInst =
    instances.find(
      (inst) =>
        String(inst.catalog_kpi_guid || '').toLowerCase() === 'kpi_cost_progress_v1',
    ) || instances[0];

  const instanceGuid = String(targetInst.instance_guid || '').trim();
  const curveMeta = getFinanceInstanceCurveMetaFromStorage(projectGuid, instanceGuid || undefined);
  const series = getFinanceInstanceSeriesFromStorage(projectGuid, instanceGuid || undefined);

  const currency = curveMeta?.currency || 'USD';
  const today = new Date().toISOString().slice(0, 10);

  // Sort series by date for lookups
  const sorted = [...series].sort((a, b) => a.reporting_date.localeCompare(b.reporting_date));

  // Resolve total budget: BOM estimate → explicit meta → last baseline in series
  let totalBudget: number | null = null;
  if (bomEstimatedTotal > 0) {
    totalBudget = bomEstimatedTotal;
  } else if (curveMeta?.total_budget && Number.isFinite(curveMeta.total_budget) && curveMeta.total_budget > 0) {
    totalBudget = curveMeta.total_budget;
  } else {
    // Fall back: last row's baseline_budget (cumulative planned total at end of project)
    const lastBaseline = [...sorted]
      .reverse()
      .find((p) => p.baseline_budget !== null && p.baseline_budget !== undefined);
    if (lastBaseline && Number.isFinite(Number(lastBaseline.baseline_budget))) {
      const b = Number(lastBaseline.baseline_budget);
      if (b > 0) totalBudget = b;
    }
  }

  // Actual spend: BOM actual rollup → finance series fallback
  let spendToDate = bomActualTotal;
  if (spendToDate <= 0) {
    const latestActual = [...sorted]
      .filter((p) => p.reporting_date <= today && p.actual_spend !== null && p.actual_spend !== undefined)
      .sort((a, b) => b.reporting_date.localeCompare(a.reporting_date))[0];
    spendToDate = latestActual ? Number(latestActual.actual_spend ?? 0) : 0;
  }

  // Color from the finance KPI indicator
  const finColorCode = Number((selectedItem as any)?.finance_indicator_color ?? 0);

  // If we have neither total budget nor spend data, don't render
  if (totalBudget === null && spendToDate === 0) return null;

  // If we have spend but no total budget, show spend-only (no progress bar)
  if (totalBudget === null || totalBudget <= 0) {
    const barColor = bucketToColor(indicatorBucket(finColorCode));
    return (
      <div
        style={{
          padding: '10px 14px 12px',
          borderRadius: 10,
          background: 'var(--bg-active)',
          border: 'var(--border-subtle)',
          marginBottom: 4,
        }}
      >
        <div
          style={{
            fontSize: 11, fontWeight: 700, letterSpacing: '0.06em',
            color: 'var(--text-tertiary)', marginBottom: 7, textTransform: 'uppercase',
          }}
        >
          {t('budget.overview.title') || 'Budget'}
        </div>
        <div style={{ display: 'flex', justifyContent: 'center', fontSize: 11, color: 'var(--text-tertiary)' }}>
          <span>
            <b style={{ fontWeight: 600, color: barColor }}>
              {t('budget.overview.spent') || 'Spent'}:
            </b>{' '}
            {fmtCurrency(spendToDate, currency)}
          </span>
        </div>
      </div>
    );
  }

  const spendPct = Math.min(Math.round((spendToDate / totalBudget) * 100), 100);
  const remaining = totalBudget - spendToDate;
  const barColor = spendPct >= 100
    ? 'var(--info-text)'
    : bucketToColor(indicatorBucket(finColorCode));

  return (
    <div
      style={{
        padding: '10px 14px 12px',
        borderRadius: 10,
        background: 'var(--bg-active)',
        border: 'var(--border-subtle)',
        marginBottom: 4,
      }}
    >
      <div
        style={{
          fontSize: 11, fontWeight: 700, letterSpacing: '0.06em',
          color: 'var(--text-tertiary)', marginBottom: 7, textTransform: 'uppercase',
        }}
      >
        {t('budget.overview.title') || 'Budget'}
      </div>

      {/* Progress bar */}
      <div style={{
        height: 10, borderRadius: 5, background: 'var(--border-subtle)',
        overflow: 'hidden', position: 'relative',
      }}>
        <div style={{
          height: '100%', borderRadius: 5,
          width: `${spendPct}%`, background: barColor,
          transition: 'width 300ms ease',
        }} />
      </div>

      {/* Labels row */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        fontSize: 11, color: 'var(--text-tertiary)', marginTop: 6,
      }}>
        <span>
          <b style={{ fontWeight: 600, color: 'var(--text-secondary)' }}>
            {t('budget.overview.spent') || 'Spent'}:
          </b>{' '}
          {fmtCurrency(spendToDate, currency)}
        </span>
        <span style={{ fontWeight: 600, color: barColor }}>{spendPct}%</span>
        <span>
          <b style={{ fontWeight: 600, color: 'var(--text-secondary)' }}>
            {t('budget.overview.total') || 'Total'}:
          </b>{' '}
          {fmtCurrency(totalBudget, currency)}
        </span>
      </div>

      {/* Remaining budget */}
      <div style={{
        fontSize: 11, color: 'var(--text-tertiary)', marginTop: 4, textAlign: 'center',
      }}>
        {fmtCurrency(remaining, currency)} {t('budget.overview.remaining') || 'remaining'}
      </div>
    </div>
  );
};

export default BudgetOverviewBar;
